import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import { Button } from 'src/lib/components/Button.component';
import { TextInput } from 'src/lib/components/TextInput.component';
import { toasting } from 'src/lib/components/Toast.component';
import { fget, fpost } from 'src/lib/fetch';
import { ADMIN_ROLE, AREA_ADMIN_ROLE } from 'src/lib/auth/roles';
import { CreateUserReq } from '../../dto/user/user.req';

export function CreateAdminUserPage() {
  const eForm = useRef<HTMLFormElement>(null);

  const eTextUsername = useRef<HTMLInputElement>(null);
  const eTextEmail = useRef<HTMLInputElement>(null);
  const eTextPassword = useRef<HTMLInputElement>(null);
  const eTextRePassword = useRef<HTMLInputElement>(null);

  const eButtonSubmit = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();
  const [role, setRole] = useState<typeof ADMIN_ROLE | typeof AREA_ADMIN_ROLE>(ADMIN_ROLE);
  const [areas, setAreas] = useState<any[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  useEffect(() => {
    fget('api/areas')
      .then(([, response]) => setAreas(response?.Items || []))
      .catch(() =>
        toasting({ children: '地域の取得に失敗しました', containerProps: { className: 'border-red-600' } }),
      );
  }, []);

  const handleButtonSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (!eForm.current?.checkValidity()) return eForm.current?.reportValidity();
    if (role === AREA_ADMIN_ROLE && selectedAreas.length === 0) {
      return toasting({ children: '地域管理者には1つ以上の地域を選択してください', containerProps: { className: 'border-red-600' } });
    }

    await fpost<CreateUserReq>('api/users', {
      body: {
        user: {
          USER_NAME: eTextUsername.current?.value || '',
          USER_MAIL: eTextEmail.current?.value || '',
          USER_PASS: eTextPassword.current?.value || '',
          USER_ROLE: role,
          USER_AREAS: role === AREA_ADMIN_ROLE ? selectedAreas : [],
          KAGAWA_EDITABLE: '0',
        },
      },
    });
    navigate('..');
  };

  return (
    <form className="flex flex-row flex-wrap px-4 pt-12 md:px-12 md:py-8" ref={eForm}>
      <h1 className="mb-2 whitespace-nowrap text-2xl">ユーザー追加</h1>
      <span className="w-full border-b border-slate-800"></span>

      <div className="flex w-1/3 flex-row flex-wrap">
        <TextInput
          labelProps={{
            children: 'ユーザー名',
          }}
          type="text"
          containerProps={{
            className: 'mt-4 w-1/3 basis-full',
            style: {
              minWidth: '24rem',
            },
          }}
          required
          ref={eTextUsername}
        />

        <TextInput
          labelProps={{
            children: 'メールアドレス',
          }}
          type="email"
          containerProps={{
            className: 'mt-4 w-1/3 basis-full',
            style: {
              minWidth: '24rem',
            },
          }}
          required
          ref={eTextEmail}
        />

        <label className="mt-4 w-1/3 basis-full text-sm text-black" htmlFor="admin-role">
          アカウント種別
        </label>
        <select
          id="admin-role"
          className="mt-2 w-full rounded-md border border-gray-300 p-2"
          value={role}
          onChange={(e) => {
            const nextRole = e.target.value as typeof role;
            setRole(nextRole);
            if (nextRole === ADMIN_ROLE) setSelectedAreas([]);
          }}>
          <option value={ADMIN_ROLE}>管理者（全地域）</option>
          <option value={AREA_ADMIN_ROLE}>地域管理者</option>
        </select>

        {role === AREA_ADMIN_ROLE && (
          <>
            <label className="mt-4 w-1/3 basis-full text-sm text-black">担当地域（複数選択可）</label>
            <ReactSelect
              isMulti
              name="areas"
              className="mt-2 w-full"
              options={areas
                .map((area) => area.AREA_NAME?.S || '')
                .filter(Boolean)
                .map((areaName) => ({ label: areaName, value: areaName }))}
              onChange={(options) => setSelectedAreas(options.map((option) => option.value || ''))}
            />
          </>
        )}

        <TextInput
          labelProps={{
            children: 'パスワード',
          }}
          type="password"
          containerProps={{
            className: 'mt-4 w-1/3 basis-full',
            style: {
              minWidth: '24rem',
            },
          }}
          onChange={(e) => {
            if (e.target.value !== eTextPassword.current?.value) {
              e.target.setCustomValidity('Password and re-password does not match.');
            } else {
              eTextPassword.current?.setCustomValidity('');
              eTextRePassword.current?.setCustomValidity('');
            }
          }}
          ref={eTextPassword}
        />

        <TextInput
          labelProps={{
            children: '再パスワード',
          }}
          type="password"
          containerProps={{
            className: 'mt-4 w-1/3 basis-full',
            style: {
              minWidth: '24rem',
            },
          }}
          onChange={(e) => {
            if (e.target.value !== eTextPassword.current?.value) {
              e.target.setCustomValidity('Password and re-password does not match.');
            } else {
              eTextPassword.current?.setCustomValidity('');
              eTextRePassword.current?.setCustomValidity('');
            }
          }}
          ref={eTextRePassword}
        />

        <div
          className="mt-8 flex w-1/3 basis-full flex-col items-center"
          style={{
            minWidth: '24rem',
          }}>
          <Button
            className="w-1/2 basis-full justify-center rounded-md bg-black text-xs text-white"
            style={{
              minWidth: '256px',
            }}
            type="submit"
            onClick={handleButtonSubmit}
            ref={eButtonSubmit}>
            追加
          </Button>
          <Link
            className="mt-4 basis-full cursor-pointer select-none text-center underline"
            to=".."
            style={{
              minWidth: '256px',
            }}>
            キャンセル
          </Link>
        </div>
      </div>
    </form>
  );
}
